import React, {
  useRef,
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
} from "react";
import { Stage, Layer, Image as KonvaImage, Rect as KonvaRect } from "react-konva";
import Konva from "konva";
import {
  Button,
  CircularProgress,
  Typography,
  Badge,
  Alert,
  IconButton,
  Tooltip,
  Box,
  Snackbar,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import AddBoxIcon from "@mui/icons-material/AddBox";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import LayersClearIcon from "@mui/icons-material/LayersClear";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import { GfFlyerItemDto, Coordinate } from "@swagger/typescript-fetch-goflyer";
import { adminApiService } from "../../utils/api";
import { useAuthHeaders } from "../../hooks/useAuthHeaders";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import { MuiConfirmationModal } from "../../app/components/MuiConfirmationModal";
import { useTranslation } from "react-i18next";
import { messages } from "../../locales/messages";
import { useImageLoader } from "./hooks/useImageLoader";
import { useCoordinateManager } from "./hooks/useCoordinateManager";
import { useViewerState } from "./hooks/useViewerState";
import { CoordinateBox } from "./components/CoordinateBox";
import { PageNavigator } from "./components/PageNavigator";
import { ThumbnailBar } from "./components/ThumbnailBar";
import { ItemSelectionDialog } from "./components/ItemSelectionDialog";
import { SaveConfirmationModal } from "./components/SaveConfirmationModal";
import { FlyerCoordinateEditorProps } from "./types";
import { EDITOR_CONSTANTS } from "./constants";
import {
  ViewerContainer,
  StageContainer,
  ControlsContainer,
  NavigationContainer,
  SaveButtonContainer,
} from "./styled";
import { FlyerCoordinateEditorContextProvider } from "./context/FlyerCoordinateEditorContext";

/**
 * Interactive flyer coordinate editor component for image display and coordinate management.
 *
 * This component provides a comprehensive canvas-based interface for viewing flyer images and
 * managing coordinate boxes that link to flyer items. It supports multi-page flyers with
 * thumbnail navigation, zoom/pan controls, and full CRUD operations on coordinate boxes.
 *
 * Key features:
 * - Canvas-based rendering for smooth interactions
 * - Multi-page flyer support with thumbnail bar and keyboard navigation
 * - Zoom and pan with mouse wheel and button controls
 * - Add, edit, move, resize, and delete coordinate boxes
 * - Visual feedback for modified/deleted boxes
 * - Batch save with confirmation modal showing all changes
 * - Item selection dialog for linking boxes to flyer items
 * - Responsive container sizing
 * - Keyboard shortcuts (Delete key for selected box)
 * - Dirty state tracking for unsaved changes
 * - Per-page and global reset functionality
 *
 * State management:
 * - useViewerState: Centralized state for viewport, modals, editor, and items
 * - useCoordinateManager: Tracks coordinate modifications with dirty checking
 * - useImageLoader: Loads and scales images with proper aspect ratios
 * - FlyerCoordinateEditorContext: Provides scale and bounds to child components
 *
 * @param props - Component properties
 * @param props.gfImages - Array of flyer images to display (one per page)
 * @param props.flyerItems - Array of flyer items with existing coordinates
 * @param props.onSave - Callback function to save coordinate changes to backend
 * @param props.containerSize - External container dimensions (optional, auto-detects if not provided)
 * @param props.onCreateNewItemRequest - Callback for creating new flyer items from selection modal
 * @param props.onEditItemRequest - Callback for editing existing flyer items
 * @returns Rendered flyer viewer component with controls and modals
 */
export const FlyerCoordinateEditor: React.FC<FlyerCoordinateEditorProps> = ({
  gfImages,
  flyerItems,
  onSave,
  containerSize: externalSize,
  onCreateNewItemRequest,
  onEditItemRequest,
  onSaveComplete,
  onRetryStarted,
  flyerId: _flyerId,
  flyerItemListId: _flyerItemListId,
  isAiGenerated,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<Konva.Stage>(null);
  const isInitialMount = useRef(true);
  const groupDragSessionRef = useRef<{
    draggedBoxId: string | null;
    draggedStartCoordinate: Coordinate | null;
    baseCoordinates: Map<string, Coordinate>;
  }>({
    draggedBoxId: null,
    draggedStartCoordinate: null,
    baseCoordinates: new Map<string, Coordinate>(),
  });

  const selectedBoxIdsRef = useRef<Set<string>>(new Set());
  const modalsRef = useRef({ selection: false, saveConfirm: false });

  const {
    state: viewerState,
    setCurrentPage,
    toggleSelectedBoxId,
    clearSelectedBoxIds,
    selectSingleBoxId,
    setSelectedBoxIds,
    setViewportPosition,
    setViewport,
    resetViewport,
    openSelectionModal: openSelectionModalFn,
    closeSelectionModal,
    openSaveConfirmModal: openSaveConfirmModalFn,
    closeSaveConfirmModal,
    setIsDraggingBox,
    setIsSaving,
    setGroupMoveEnabled,
    setMultiSelectEnabled,
    addNewItem,
    removeNewItem,
    clearNewItems,
    clearDeletedItems,
    deleteItem,
    restoreItem,
    allFlyerItems: getAllFlyerItems,
    itemsWithCoordinates: getItemsWithCoordinates,
  } = useViewerState();

  const {
    currentPage,
    selectedBoxIds,
    isDraggingBox,
    isSaving,
    groupMoveEnabled,
    multiSelectEnabled,
  } = viewerState.editor;
  const { scale: stageScale, position: stagePosition } = viewerState.viewport;
  const { selection: openSelectionModal, saveConfirm: openSaveConfirmModal } =
    viewerState.modals;
  const { newItems, deletedItemIds } = viewerState.items;

  useEffect(() => {
    selectedBoxIdsRef.current = selectedBoxIds;
  }, [selectedBoxIds]);

  useEffect(() => {
    modalsRef.current = {
      selection: openSelectionModal,
      saveConfirm: openSaveConfirmModal,
    };
  }, [openSelectionModal, openSaveConfirmModal]);

  const [containerSize, setContainerSize] = useState({
    width: EDITOR_CONSTANTS.DEFAULT_CONTAINER_WIDTH,
    height: EDITOR_CONSTANTS.DEFAULT_CONTAINER_HEIGHT,
  });

  const [aiExtractionCounter, setAiExtractionCounter] = useState(0);
  const [retryingImageId, setRetryingImageId] = useState<string | null>(null);
  const [retryMessage, setRetryMessage] = useState<{
    text: string;
    severity: "success" | "error" | "info";
  } | null>(null);
  const [openRetryConfirmModal, setOpenRetryConfirmModal] = useState(false);
  const [selectionBox, setSelectionBox] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const selectionStartRef = useRef<{ x: number; y: number } | null>(null);
  const isRightSelectingRef = useRef(false);

  const authHeaders = useAuthHeaders();
  const { t } = useTranslation();

  useLayoutEffect(() => {
    if (externalSize && isInitialMount.current) {
      setContainerSize({
        width: externalSize.width,
        height: externalSize.height,
      });

      setViewport({
        scale: 1,
        position: { x: 0, y: 0 },
      });

      isInitialMount.current = false;
    } else if (
      externalSize &&
      !isInitialMount.current &&
      containerSize.width !== EDITOR_CONSTANTS.DEFAULT_CONTAINER_WIDTH
    ) {
      // Only run resize logic if containerSize has been properly initialized (not default)
      // Calculate scale to fit content in new viewport
      const scaleX = externalSize.width / containerSize.width;
      const scaleY = externalSize.height / containerSize.height;
      const newScale = Math.min(scaleX, scaleY);

      // Calculate centered position
      const scaledContentWidth = containerSize.width * newScale;
      const scaledContentHeight = containerSize.height * newScale;
      const newX = (externalSize.width - scaledContentWidth) / 2;
      const newY = (externalSize.height - scaledContentHeight) / 2;

      setViewport({
        scale: newScale,
        position: { x: newX, y: newY },
      });
    }
  }, [externalSize?.width, externalSize?.height, setViewport, containerSize]);

  const { loadedImages, isLoading, error } = useImageLoader({
    gfImages,
    containerWidth: containerSize.width,
    containerHeight: containerSize.height,
  });

  const currentImageData = loadedImages.get(currentPage + 1);

  const allFlyerItems = getAllFlyerItems(flyerItems);

  const itemsWithCoordinates = getItemsWithCoordinates(flyerItems);

  const coordinateManager = useCoordinateManager({
    flyerItems: allFlyerItems,
    currentPage: currentImageData?.flyerIndex || currentPage + 1,
    imageBounds: {
      width: currentImageData?.naturalWidth || 0,
      height: currentImageData?.naturalHeight || 0,
    },
  });

  const itemsForCurrentPage = React.useMemo(() => {
    return coordinateManager
      .getItemsForCurrentPage()
      .filter((item) => !deletedItemIds.has(item.id!));
  }, [coordinateManager, deletedItemIds, coordinateManager.isDirty]);

  // Lets the session-expired dialog warn before a sign-out drops these edits.
  useUnsavedChanges(coordinateManager.isDirty || deletedItemIds.size > 0);

  const contextValue = useMemo(
    () => ({
      scaleX: currentImageData?.scaleX || 1,
      scaleY: currentImageData?.scaleY || 1,
      imageBounds: {
        width: currentImageData?.naturalWidth || 0,
        height: currentImageData?.naturalHeight || 0,
      },
      imageOffset: {
        x: (containerSize.width - (currentImageData?.width || 0)) / 2,
        y: (containerSize.height - (currentImageData?.height || 0)) / 2,
      },
      multiSelectEnabled,
    }),
    [
      currentImageData?.scaleX,
      currentImageData?.scaleY,
      currentImageData?.naturalWidth,
      currentImageData?.naturalHeight,
      currentImageData?.width,
      currentImageData?.height,
      containerSize.width,
      containerSize.height,
      multiSelectEnabled,
    ],
  );

  const handleBoxSelect = (boxId: string) => {
    if (multiSelectEnabled) {
      toggleSelectedBoxId(boxId);
      return;
    }

    if (selectedBoxIds.has(boxId) && selectedBoxIds.size === 1) {
      clearSelectedBoxIds();
    } else {
      selectSingleBoxId(boxId);
    }
  };

  const totalPages = Math.max(1, loadedImages.size);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const currentModals = modalsRef.current;
      const currentSelectedId = selectedBoxIdsRef.current;

      if (currentModals.selection || currentModals.saveConfirm) {
        return;
      }

      if (e.key === "Delete" && currentSelectedId.size > 0) {
        handleDeleteBox();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  /**
   * Opens the save confirmation modal to review changes before saving.
   *
   * Retrieves all modified coordinates and deleted items from state managers,
   * then opens a modal displaying a categorized summary of changes (creates,
   * updates, deletes). Only proceeds if there are actual changes to save.
   *
   * Changes include:
   * - New coordinate boxes added
   * - Existing coordinates moved or resized
   * - Coordinate boxes marked for deletion
   */
  const handleSave = async () => {
    const changes = coordinateManager.getModifiedCoordinates();
    const deletions = Array.from(deletedItemIds);

    if (changes.length === 0 && deletions.length === 0) return;

    openSaveConfirmModalFn();
  };

  /**
   * Saves all coordinate changes and deletions after user confirmation.
   *
   * Processes both coordinate modifications and deletions into a unified change array.
   * Deleted items are converted to coordinate changes with null modifiedCoordinate.
   * Calls the onSave prop with all changes for backend persistence.
   *
   * On success:
   * - Resets coordinate manager dirty state
   * - Clears deleted items set
   * - Clears temporary new items
   * - Deselects current box
   * - Closes save confirmation modal
   *
   * On error:
   * - Logs error to console
   * - Leaves changes in place for retry
   *
   * Always sets isSaving flag appropriately for loading states.
   */
  const handleConfirmSave = async () => {
    const changes = coordinateManager.getModifiedCoordinates();
    const deletions = Array.from(deletedItemIds);

    setIsSaving(true);
    try {
      const allChanges = [
        ...changes,
        ...deletions
          .map((itemId) => {
            const item = flyerItems.find((i) => i.id === itemId);
            if (!item?.coordinate) return null;

            return {
              flyerItemId: itemId,
              flyerItem: item,
              originalCoordinate: item.coordinate,
              modifiedCoordinate: null as unknown as Coordinate,
            };
          })
          .filter(
            (change): change is NonNullable<typeof change> => change !== null,
          ),
      ];

      await onSave(allChanges);
      coordinateManager.resetChanges();
      clearDeletedItems();
      clearNewItems();
      clearSelectedBoxIds();
      closeSaveConfirmModal();
      onSaveComplete?.();
    } catch (error) {
      console.error("Error saving coordinates:", error);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handles mouse wheel zoom with pointer-based zoom origin.
   *
   * Implements smooth zooming centered on the mouse cursor position.
   * Calculates the point under the cursor before zoom, applies the new scale,
   * then adjusts viewport position to keep that point stationary.
   *
   * Zoom behavior:
   * - Scroll down: zoom out (divide by WHEEL_ZOOM_STEP)
   * - Scroll up: zoom in (multiply by WHEEL_ZOOM_STEP)
   * - Clamped between ZOOM_MIN and ZOOM_MAX
   * - Prevents default scroll behavior
   *
   * @param e - Konva wheel event containing deltaY and pointer position
   */
  const handleWheel = (e: Konva.KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();

    const stage = stageRef.current;
    if (!stage) return;

    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();

    if (!pointer) return;

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    const scaleBy = EDITOR_CONSTANTS.WHEEL_ZOOM_STEP;
    const newScale = e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;

    const clampedScale = Math.max(
      EDITOR_CONSTANTS.ZOOM_MIN,
      Math.min(EDITOR_CONSTANTS.ZOOM_MAX, newScale),
    );

    setViewport({
      scale: clampedScale,
      position: {
        x: pointer.x - mousePointTo.x * clampedScale,
        y: pointer.y - mousePointTo.y * clampedScale,
      },
    });
  };

  /**
   * Zooms in toward the center of the viewport.
   *
   * Multiplies current scale by ZOOM_STEP and adjusts viewport position
   * to keep the center point stationary. Zoom is clamped at ZOOM_MAX.
   * This creates a smooth zoom-to-center effect when using button controls.
   */
  const handleZoomIn = () => {
    const stage = stageRef.current;
    if (!stage) return;

    const oldScale = stageScale;
    const newScale = Math.min(
      EDITOR_CONSTANTS.ZOOM_MAX,
      stageScale * EDITOR_CONSTANTS.ZOOM_STEP,
    );

    const center = {
      x: containerSize.width / 2,
      y: containerSize.height / 2,
    };

    const mousePointTo = {
      x: (center.x - stagePosition.x) / oldScale,
      y: (center.y - stagePosition.y) / oldScale,
    };

    setViewport({
      scale: newScale,
      position: {
        x: center.x - mousePointTo.x * newScale,
        y: center.y - mousePointTo.y * newScale,
      },
    });
  };

  /**
   * Zooms out from the center of the viewport.
   *
   * Divides current scale by ZOOM_STEP and adjusts viewport position
   * to keep the center point stationary. Zoom is clamped at ZOOM_MIN.
   * This creates a smooth zoom-from-center effect when using button controls.
   */
  const handleZoomOut = () => {
    const stage = stageRef.current;
    if (!stage) return;

    const oldScale = stageScale;
    const newScale = Math.max(
      EDITOR_CONSTANTS.ZOOM_MIN,
      stageScale / EDITOR_CONSTANTS.ZOOM_STEP,
    );

    const center = {
      x: containerSize.width / 2,
      y: containerSize.height / 2,
    };

    const mousePointTo = {
      x: (center.x - stagePosition.x) / oldScale,
      y: (center.y - stagePosition.y) / oldScale,
    };

    setViewport({
      scale: newScale,
      position: {
        x: center.x - mousePointTo.x * newScale,
        y: center.y - mousePointTo.y * newScale,
      },
    });
  };

  /**
   * Resets zoom and pan to centered state based on current window size.
   *
   * Calculates the optimal scale to fit the loaded image content in the current viewport,
   * then centers it. This accounts for window resizes - if the window is larger than the
   * original loaded size, it scales and centers appropriately.
   */
  const handleResetZoom = () => {
    if (!externalSize) {
      resetViewport();
      return;
    }

    // Calculate scale to fit content in current viewport
    const scaleX = externalSize.width / containerSize.width;
    const scaleY = externalSize.height / containerSize.height;
    const newScale = Math.min(scaleX, scaleY);

    // Calculate centered position
    const scaledContentWidth = containerSize.width * newScale;
    const scaledContentHeight = containerSize.height * newScale;
    const newX = (externalSize.width - scaledContentWidth) / 2;
    const newY = (externalSize.height - scaledContentHeight) / 2;

    setViewport({
      scale: newScale,
      position: { x: newX, y: newY },
    });
  };

  /**
   * Deselects coordinate box when clicking on empty stage area.
   *
   * Checks if the click target is the stage itself (not a shape).
   * If true, clears the selected box ID to deselect any active box.
   * This allows users to deselect by clicking empty space.
   *
   * @param e - Konva mouse event containing target information
   */
  const handleStageClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (e.evt.button === 2 || isRightSelectingRef.current) return;

    const clickedOnEmpty = e.target === e.target.getStage();
    if (clickedOnEmpty) {
      clearSelectedBoxIds();
    }
  };

  const getStagePointerLocalPosition = () => {
    const stage = stageRef.current;
    if (!stage) return null;
    const pointer = stage.getPointerPosition();
    if (!pointer) return null;

    return {
      x: (pointer.x - stagePosition.x) / stageScale,
      y: (pointer.y - stagePosition.y) / stageScale,
    };
  };

  const handleStageMouseDown = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (e.evt.button !== 2 || !multiSelectEnabled) return;

    e.evt.preventDefault();
    const start = getStagePointerLocalPosition();
    if (!start) return;

    isRightSelectingRef.current = true;
    selectionStartRef.current = start;
    setSelectionBox({ x: start.x, y: start.y, width: 0, height: 0 });
  };

  const handleStageMouseMove = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!isRightSelectingRef.current || !selectionStartRef.current) return;

    e.evt.preventDefault();
    const current = getStagePointerLocalPosition();
    if (!current) return;

    const start = selectionStartRef.current;
    const nextX = Math.min(start.x, current.x);
    const nextY = Math.min(start.y, current.y);
    const nextWidth = Math.abs(current.x - start.x);
    const nextHeight = Math.abs(current.y - start.y);

    setSelectionBox({
      x: nextX,
      y: nextY,
      width: nextWidth,
      height: nextHeight,
    });
  };

  const handleStageMouseUp = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!isRightSelectingRef.current || e.evt.button !== 2) return;

    e.evt.preventDefault();
    const start = selectionStartRef.current;
    const end = getStagePointerLocalPosition();
    if (!start || !end) {
      isRightSelectingRef.current = false;
      selectionStartRef.current = null;
      setSelectionBox(null);
      return;
    }

    const box = {
      x: Math.min(start.x, end.x),
      y: Math.min(start.y, end.y),
      width: Math.abs(end.x - start.x),
      height: Math.abs(end.y - start.y),
    };
    isRightSelectingRef.current = false;
    selectionStartRef.current = null;
    setSelectionBox(null);

    if (!box || box.width < 4 || box.height < 4) return;

    const imageOffsetX = (containerSize.width - (currentImageData?.width || 0)) / 2;
    const imageOffsetY =
      (containerSize.height - (currentImageData?.height || 0)) / 2;
    const scaleX = currentImageData?.scaleX || 1;
    const scaleY = currentImageData?.scaleY || 1;

    const selectedIds = new Set<string>();
    itemsForCurrentPage.forEach((item) => {
      const id = item.id;
      if (!id) return;
      const coord = coordinateManager.getCoordinate(id);
      if (!coord) return;

      const itemX = imageOffsetX + coord.x * scaleX;
      const itemY = imageOffsetY + coord.y * scaleY;
      const itemWidth = coord.component.width * scaleX;
      const itemHeight = coord.component.height * scaleY;

      const isFullyInside =
        itemX >= box.x &&
        itemY >= box.y &&
        itemX + itemWidth <= box.x + box.width &&
        itemY + itemHeight <= box.y + box.height;

      if (isFullyInside) {
        selectedIds.add(id);
      }
    });

    if (selectedIds.size === 0) {
      clearSelectedBoxIds();
      return;
    }

    if (multiSelectEnabled) {
      setSelectedBoxIds(selectedIds);
    } else {
      const firstId = selectedIds.values().next().value;
      if (firstId) {
        selectSingleBoxId(firstId);
      }
    }
  };

  useEffect(() => {
    const clearRightSelection = () => {
      if (!isRightSelectingRef.current) return;
      isRightSelectingRef.current = false;
      selectionStartRef.current = null;
      setSelectionBox(null);
    };

    window.addEventListener("mouseup", clearRightSelection);
    window.addEventListener("blur", clearRightSelection);

    return () => {
      window.removeEventListener("mouseup", clearRightSelection);
      window.removeEventListener("blur", clearRightSelection);
    };
  }, []);

  /**
   * Opens the item selection modal to add a new coordinate box.
   *
   * Displays a searchable dialog of all available flyer items, filtered to show
   * only items without coordinates on the current page. Users can select an existing
   * item to add a coordinate, restore a deleted item, or create a new item.
   */
  const handleAddBox = () => {
    openSelectionModalFn();
  };

  const handleBoxDragStart = (draggedBoxId: string, coordinate: Coordinate) => {
    setIsDraggingBox(true);

    const shouldMoveAsGroup =
      groupMoveEnabled &&
      selectedBoxIds.size > 1 &&
      selectedBoxIds.has(draggedBoxId);

    if (!shouldMoveAsGroup) {
      groupDragSessionRef.current = {
        draggedBoxId: null,
        draggedStartCoordinate: null,
        baseCoordinates: new Map<string, Coordinate>(),
      };
      return;
    }

    const baseCoordinates = new Map<string, Coordinate>();
    selectedBoxIds.forEach((selectedId) => {
      const selectedCoordinate = coordinateManager.getCoordinate(selectedId);
      if (!selectedCoordinate) return;

      baseCoordinates.set(selectedId, { ...selectedCoordinate });
    });

    groupDragSessionRef.current = {
      draggedBoxId,
      draggedStartCoordinate:
        baseCoordinates.get(draggedBoxId) || { ...coordinate },
      baseCoordinates,
    };
  };

  const clampCoordinateToBounds = (coordinate: Coordinate): Coordinate => {
    const imageWidth = currentImageData?.naturalWidth || 0;
    const imageHeight = currentImageData?.naturalHeight || 0;
    const maxX = Math.max(0, imageWidth - coordinate.component.width);
    const maxY = Math.max(0, imageHeight - coordinate.component.height);

    return {
      ...coordinate,
      x: Math.max(0, Math.min(coordinate.x, maxX)),
      y: Math.max(0, Math.min(coordinate.y, maxY)),
    };
  };

  const handleBoxDragEnd = (
    draggedBoxId: string,
    previousCoordinate: Coordinate,
    newCoordinate: Coordinate,
  ) => {
    const clampedDraggedCoordinate = clampCoordinateToBounds(newCoordinate);
    const shouldMoveAsGroup =
      groupMoveEnabled &&
      selectedBoxIds.size > 1 &&
      selectedBoxIds.has(draggedBoxId);

    if (!shouldMoveAsGroup) {
      coordinateManager.updateCoordinate(draggedBoxId, clampedDraggedCoordinate);
      groupDragSessionRef.current = {
        draggedBoxId: null,
        draggedStartCoordinate: null,
        baseCoordinates: new Map<string, Coordinate>(),
      };
      return;
    }

    const sessionStartCoordinate =
      groupDragSessionRef.current.draggedBoxId === draggedBoxId &&
      groupDragSessionRef.current.draggedStartCoordinate
        ? groupDragSessionRef.current.draggedStartCoordinate
        : previousCoordinate;

    const deltaX = clampedDraggedCoordinate.x - sessionStartCoordinate.x;
    const deltaY = clampedDraggedCoordinate.y - sessionStartCoordinate.y;

    selectedBoxIds.forEach((selectedId) => {
      const selectedCoordinate =
        groupDragSessionRef.current.baseCoordinates.get(selectedId) ||
        coordinateManager.getCoordinate(selectedId);
      if (!selectedCoordinate) return;

      if (selectedId === draggedBoxId) {
        coordinateManager.updateCoordinate(selectedId, clampedDraggedCoordinate);
        return;
      }

      const translatedCoordinate: Coordinate = {
        ...selectedCoordinate,
        x: selectedCoordinate.x + deltaX,
        y: selectedCoordinate.y + deltaY,
      };

      coordinateManager.updateCoordinate(
        selectedId,
        clampCoordinateToBounds(translatedCoordinate),
      );
    });

    groupDragSessionRef.current = {
      draggedBoxId: null,
      draggedStartCoordinate: null,
      baseCoordinates: new Map<string, Coordinate>(),
    };
  };

  const handleBoxDragMove = (
    draggedBoxId: string,
    previousCoordinate: Coordinate,
    newCoordinate: Coordinate,
  ) => {
    const shouldMoveAsGroup =
      groupMoveEnabled &&
      selectedBoxIds.size > 1 &&
      selectedBoxIds.has(draggedBoxId);

    if (!shouldMoveAsGroup) {
      return;
    }

    const sessionStartCoordinate =
      groupDragSessionRef.current.draggedBoxId === draggedBoxId &&
      groupDragSessionRef.current.draggedStartCoordinate
        ? groupDragSessionRef.current.draggedStartCoordinate
        : previousCoordinate;

    const clampedDraggedCoordinate = clampCoordinateToBounds(newCoordinate);
    const deltaX = clampedDraggedCoordinate.x - sessionStartCoordinate.x;
    const deltaY = clampedDraggedCoordinate.y - sessionStartCoordinate.y;

    selectedBoxIds.forEach((selectedId) => {
      if (selectedId === draggedBoxId) return;

      const selectedCoordinate =
        groupDragSessionRef.current.baseCoordinates.get(selectedId) ||
        coordinateManager.getCoordinate(selectedId);
      if (!selectedCoordinate) return;

      const translatedCoordinate: Coordinate = {
        ...selectedCoordinate,
        x: selectedCoordinate.x + deltaX,
        y: selectedCoordinate.y + deltaY,
      };

      coordinateManager.updateCoordinate(
        selectedId,
        clampCoordinateToBounds(translatedCoordinate),
      );
    });
  };

  /**
   * Handles item selection from the modal, creating a new coordinate at viewport center.
   *
   * When a user selects an item from the selection modal:
   * - If item is marked as deleted, restores it instead of creating new coordinate
   * - Otherwise, creates a new coordinate box at the center of the current viewport
   * - Box is created with default width and height from EDITOR_CONSTANTS
   * - Position is calculated based on current zoom and pan state to appear in visible area
   * - Automatically selects the new box for immediate editing
   * - Closes the selection modal
   *
   * The coordinate is added to the coordinate manager's modification tracking,
   * making it part of the unsaved changes until save is confirmed.
   *
   * @param item - The flyer item to link with a new coordinate box
   */
  const handleSelectItem = (item: GfFlyerItemDto) => {
    if (!currentImageData) {
      return;
    }

    if (item.id && deletedItemIds.has(item.id)) {
      restoreItem(item.id);
      selectSingleBoxId(item.id);
      closeSelectionModal();
      return;
    }

    // Calculate the center of the viewport in screen coordinates
    const viewportCenterScreenX = containerSize.width / 2;
    const viewportCenterScreenY = containerSize.height / 2;

    // Calculate image offset (centered position within container)
    const imageOffsetX = (containerSize.width - currentImageData.width) / 2;
    const imageOffsetY = (containerSize.height - currentImageData.height) / 2;

    // Convert viewport center from screen space to natural image coordinates
    // Step 1: Convert from screen space to stage space (account for stage pan and zoom)
    const stageCenterX = (viewportCenterScreenX - stagePosition.x) / stageScale;
    const stageCenterY = (viewportCenterScreenY - stagePosition.y) / stageScale;

    // Step 2: Convert from stage space to natural image coordinates
    // Subtract image offset and divide by image scale factors
    const imageCenterX =
      (stageCenterX - imageOffsetX) / currentImageData.scaleX;
    const imageCenterY =
      (stageCenterY - imageOffsetY) / currentImageData.scaleY;

    // Step 3: Position box center at viewport center (subtract half box dimensions)
    const centerX = Math.round(
      imageCenterX - EDITOR_CONSTANTS.DEFAULT_BOX_WIDTH / 2,
    );
    const centerY = Math.round(
      imageCenterY - EDITOR_CONSTANTS.DEFAULT_BOX_HEIGHT / 2,
    );

    const newCoordinate: Coordinate = {
      x: centerX,
      y: centerY,
      flyerPage: currentImageData.flyerIndex,
      component: {
        width: EDITOR_CONSTANTS.DEFAULT_BOX_WIDTH,
        height: EDITOR_CONSTANTS.DEFAULT_BOX_HEIGHT,
      } as Coordinate["component"],
    };

    coordinateManager.updateCoordinate(item.id!, newCoordinate);
    selectSingleBoxId(item.id!);
    closeSelectionModal();
  };

  /**
   * Handles request to create a new flyer item from the selection modal.
   *
   * Closes the selection modal and invokes the onCreateNewItemRequest callback prop
   * with a continuation function. The parent component should display a create item
   * form/modal, and upon successful creation, call the provided callback with the
   * new item. This will then add a coordinate box for the newly created item.
   *
   * Flow:
   * 1. User clicks "Create New Item" in selection modal
   * 2. This function closes selection modal
   * 3. Parent opens create item form via onCreateNewItemRequest
   * 4. Parent creates item and calls provided callback
   * 5. handleSelectItem is called with new item
   * 6. Coordinate box is added for the new item
   *
   * Logs a warning if onCreateNewItemRequest prop is not provided.
   */
  const handleCreateNewFromModal = () => {
    closeSelectionModal();

    if (onCreateNewItemRequest) {
      onCreateNewItemRequest((newItem: GfFlyerItemDto) => {
        handleSelectItem(newItem);
      });
    } else {
      console.warn("onCreateNewItemRequest prop not provided");
    }
  };

  /**
   * Handles AI extraction request from the selection modal.
   *
   * Creates a temporary placeholder flyer item with a unique AI extraction ID,
   * then creates a coordinate box at the viewport center marked for AI extraction.
   * The coordinate will be sent to the AI extraction API during save instead of
   * being saved as a regular coordinate.
   */
  const handleAIExtract = () => {
    if (!currentImageData) {
      return;
    }

    closeSelectionModal();

    // Create temporary AI extraction item with unique ID
    const aiExtractId = `${EDITOR_CONSTANTS.AI_EXTRACT_TEMP_PREFIX}${Date.now()}-${aiExtractionCounter}`;
    setAiExtractionCounter((prev) => prev + 1);

    const tempAIItem = {
      id: aiExtractId,
      name: "AI Extraction",
      nameChinese: "AI 提取",
      isDeleted: false,
    } as GfFlyerItemDto;

    const viewportCenterScreenX = containerSize.width / 2;
    const viewportCenterScreenY = containerSize.height / 2;

    const imageOffsetX = (containerSize.width - currentImageData.width) / 2;
    const imageOffsetY = (containerSize.height - currentImageData.height) / 2;

    const stageCenterX = (viewportCenterScreenX - stagePosition.x) / stageScale;
    const stageCenterY = (viewportCenterScreenY - stagePosition.y) / stageScale;

    const imageCenterX =
      (stageCenterX - imageOffsetX) / currentImageData.scaleX;
    const imageCenterY =
      (stageCenterY - imageOffsetY) / currentImageData.scaleY;

    const centerX = Math.round(
      imageCenterX - EDITOR_CONSTANTS.DEFAULT_BOX_WIDTH / 2,
    );
    const centerY = Math.round(
      imageCenterY - EDITOR_CONSTANTS.DEFAULT_BOX_HEIGHT / 2,
    );

    const newCoordinate: Coordinate = {
      x: centerX,
      y: centerY,
      flyerPage: currentImageData.flyerIndex,
      component: {
        width: EDITOR_CONSTANTS.DEFAULT_BOX_WIDTH,
        height: EDITOR_CONSTANTS.DEFAULT_BOX_HEIGHT,
      } as Coordinate["component"],
    };

    // Add temporary item to state
    addNewItem(tempAIItem);

    // Mark coordinate as AI extraction
    coordinateManager.updateCoordinate(aiExtractId, newCoordinate, true);
    selectSingleBoxId(aiExtractId);
  };

  /**
   * Deletes the currently selected coordinate box or marks it for deletion.
   *
   * Behavior depends on the type of box being deleted:
   * - Temporary boxes (ID starts with "temp_"): Immediately removed from newItems array
   * - AI extraction boxes (ID starts with "ai-extract-temp-"): Removed from newItems and coordinates reset
   * - Newly added coordinates (no original coordinate): Reset the coordinate changes, removing it from modified state
   * - Persisted boxes with original coordinates: Added to deletedItemIds set for batch deletion on save
   *
   * The box is removed from view immediately, but persisted boxes can be restored
   * via "undo" or by re-adding the item from the selection modal. Changes are not
   * permanent until save is confirmed.
   *
   * After deletion, clears the selected box ID to deselect.
   * Can be triggered by clicking the delete button or pressing the Delete key.
   */
  const handleDeleteBox = () => {
    if (selectedBoxIds.size === 0) return;

    selectedBoxIds.forEach((boxId) => {
      if (
        boxId.startsWith(EDITOR_CONSTANTS.TEMP_ITEM_PREFIX) ||
        boxId.startsWith(EDITOR_CONSTANTS.AI_EXTRACT_TEMP_PREFIX)
      ) {
        // Remove temporary or AI extraction items from newItems
        removeNewItem(boxId);
        // Also reset the coordinate to clean up AI extraction flags
        coordinateManager.resetCoordinate(boxId);
      } else {
        // Check if this is a newly added coordinate (no original coordinate exists)
        const item = flyerItems.find((i) => i.id === boxId);
        const hasOriginalCoordinate =
          item?.coordinate !== undefined && item?.coordinate !== null;

        if (!hasOriginalCoordinate) {
          // This is a newly added coordinate, just reset it (remove from modified state)
          coordinateManager.resetCoordinate(boxId);
        } else {
          // This has an original coordinate, mark it for deletion
          // Also reset any modifications since deletion supersedes them
          coordinateManager.resetCoordinate(boxId);
          deleteItem(boxId);
        }
      }
    });

    clearSelectedBoxIds();
  };

  /**
   * Resets all unsaved changes for the current page only.
   *
   * Iterates through all items with coordinates on the current page and:
   * - Reverts any coordinate modifications back to original values
   * - Restores any deleted items back to visible state
   * - Resets newly added coordinate boxes (items without original coordinates)
   * - Clears selection
   *
   * This provides a page-scoped undo operation, useful when user wants to
   * discard changes on one page without affecting other pages. Changes on
   * other pages remain intact.
   *
   * Button is disabled if there are no changes on the current page.
   */
  const handleResetCurrentPage = () => {
    if (!currentImageData) return;

    const currentFlyerIndex = currentImageData.flyerIndex;

    // Reset items that originally had coordinates on this page
    const itemsOnCurrentPage = allFlyerItems.filter(
      (item) => item.coordinate?.flyerPage === currentFlyerIndex,
    );

    itemsOnCurrentPage.forEach((item) => {
      if (item.id) {
        coordinateManager.resetCoordinate(item.id);
        if (deletedItemIds.has(item.id)) {
          restoreItem(item.id);
        }
      }
    });

    // Also reset newly added coordinates on this page (items without original coordinates)
    const modifiedChanges = coordinateManager.getModifiedCoordinates();
    modifiedChanges.forEach((change) => {
      if (
        change.modifiedCoordinate.flyerPage === currentFlyerIndex &&
        !change.originalCoordinate
      ) {
        coordinateManager.resetCoordinate(change.flyerItemId);
      }
    });

    clearSelectedBoxIds();
  };

  /**
   * Resets all unsaved changes across all pages.
   *
   * Performs a complete reset of the editor state:
   * - Reverts all coordinate modifications via coordinateManager.resetChanges()
   * - Clears all deleted items from deletedItemIds set
   * - Removes all temporary new items
   * - Clears selection
   *
   * This is a global undo operation that discards all changes made since the
   * last save. Useful when user wants to start fresh or accidentally made
   * unwanted modifications.
   *
   * Button is disabled if there are no changes across any pages.
   */
  const handleResetAll = () => {
    coordinateManager.resetChanges();
    clearDeletedItems();
    clearNewItems();
    clearSelectedBoxIds();
  };

  /**
   * Opens confirmation modal before retrying AI coordinate extraction.
   *
   * Validates that required data exists before showing the confirmation modal.
   * This prevents accidental triggering of the retry operation.
   */
  const handleRetryImage = () => {
    if (!isAiGenerated) return;

    if (!_flyerItemListId || !_flyerId || !currentImageData) {
      setRetryMessage({
        text: "Missing required data to retry image",
        severity: "error",
      });
      return;
    }

    // Find the image that matches the current flyerIndex
    const currentImage = gfImages.find(
      (img) => img.flyerIndex === currentImageData.flyerIndex,
    );
    console.log("currentImage", currentImage);
    if (!currentImage?.id) {
      setRetryMessage({
        text: "Current image has no ID",
        severity: "error",
      });
      return;
    }

    setOpenRetryConfirmModal(true);
  };

  /**
   * Confirms and executes AI coordinate extraction retry for the current flyer image.
   *
   * Calls the backend API to re-run coordinate extraction for the currently visible image.
   * This is useful when:
   * - Previous coordinate extraction failed
   * - Coordinates need to be regenerated with updated AI models
   * - Manual coordinate adjustments need AI assistance
   *
   * Requirements:
   * - flyerItemListId must be provided as prop
   * - Current image must have a valid ID
   *
   * On success: Shows success message with job ID
   * On error: Shows error message with details
   */
  const handleConfirmRetryImage = async () => {
    setOpenRetryConfirmModal(false);

    if (!_flyerItemListId || !_flyerId || !currentImageData) {
      setRetryMessage({
        text: "Missing flyerItemListId or flyerId",
        severity: "error",
      });
      return;
    }

    // Find the image that matches the current flyerIndex
    const currentImage = gfImages.find(
      (img) => img.flyerIndex === currentImageData.flyerIndex,
    );
    if (!currentImage?.id) {
      setRetryMessage({
        text: "Current image has no ID",
        severity: "error",
      });
      return;
    }

    setRetryingImageId(currentImage.id);
    setRetryMessage({
      text: "Retrying coordinate extraction...",
      severity: "info",
    });

    try {
      const response =
        await adminApiService.gfFlyerAdminControllerRetryImageCoordinates(
          {
            flyerItemListId: _flyerItemListId,
            imageId: currentImage.id,
          },
          _flyerId!,
          authHeaders,
        );

      setRetryMessage({
        text: `Retry started successfully. Job ID: ${response.jobId}`,
        severity: "success",
      });

      // Reset editor data
      coordinateManager.resetChanges();
      clearDeletedItems();
      clearNewItems();
      clearSelectedBoxIds();

      // Notify parent that retry has started
      if (onRetryStarted && response.jobId) {
        onRetryStarted(response.jobId);
      }
    } catch (error: any) {
      console.error("Error retrying image coordinates:", error);
      setRetryMessage({
        text: `Failed to retry: ${error?.message || "Unknown error"}`,
        severity: "error",
      });
    } finally {
      setRetryingImageId(null);
    }
  };

  if (error) {
    console.error("[FlyerCoordinateEditor] Error state:", error);
    return (
      <ViewerContainer data-testid="flyer-coordinate-editor-error">
        <Alert
          severity="error"
          sx={{ m: 2 }}
          data-testid="flyer-coordinate-error-alert"
        >
          {error}
        </Alert>
      </ViewerContainer>
    );
  }

  if (isLoading || !currentImageData) {
    return (
      <ViewerContainer data-testid="flyer-coordinate-editor-loading">
        <StageContainer data-testid="flyer-coordinate-stage-container-loading">
          <CircularProgress
            sx={{ color: "#ff8038" }}
            data-testid="flyer-coordinate-loading-spinner"
          />
          <Typography
            variant="body2"
            sx={{ mt: 2, textTransform: "capitalize" }}
          >
            Loading flyer images...
          </Typography>
        </StageContainer>
      </ViewerContainer>
    );
  }

  // Use externalSize for visual Stage dimensions
  // Use containerSize for image loading (only updates on significant changes >10%)
  const stageWidth = externalSize?.width || containerSize.width;
  const stageHeight = externalSize?.height || containerSize.height;

  return (
    <ViewerContainer data-testid="flyer-coordinate-editor">
      <StageContainer
        ref={containerRef}
        data-testid="flyer-coordinate-stage-container"
      >
        <Stage
          ref={stageRef}
          width={stageWidth}
          height={stageHeight}
          scaleX={stageScale}
          scaleY={stageScale}
          x={stagePosition.x}
          y={stagePosition.y}
          draggable={!isDraggingBox && !isRightSelectingRef.current}
          onWheel={handleWheel}
          onContextMenu={(e) => e.evt.preventDefault()}
          onMouseDown={handleStageMouseDown}
          onMouseMove={handleStageMouseMove}
          onMouseUp={handleStageMouseUp}
          onClick={handleStageClick}
          onTap={handleStageClick}
          onDragEnd={(e) => {
            if (!isDraggingBox) {
              setViewportPosition({
                x: e.target.x(),
                y: e.target.y(),
              });
            }
          }}
        >
          <FlyerCoordinateEditorContextProvider value={contextValue}>
            <Layer>
              <KonvaImage
                image={currentImageData.image}
                x={(containerSize.width - currentImageData.width) / 2}
                y={(containerSize.height - currentImageData.height) / 2}
                width={currentImageData.width}
                height={currentImageData.height}
              />
              {selectionBox && (
                <KonvaRect
                  x={selectionBox.x}
                  y={selectionBox.y}
                  width={selectionBox.width}
                  height={selectionBox.height}
                  stroke="#1976d2"
                  strokeWidth={1}
                  dash={[4, 4]}
                  fill="rgba(25,118,210,0.1)"
                  listening={false}
                />
              )}

              {itemsForCurrentPage
                .filter((item) => !selectedBoxIds.has(item.id!))
                .map((item) => {
                  const coordinate = coordinateManager.getCoordinate(item.id!);
                  if (!coordinate) return null;

                  return (
                    <CoordinateBox
                      key={item.id}
                      flyerItem={item}
                      coordinate={coordinate}
                      isSelected={false}
                      isAIExtraction={coordinateManager.isAIExtraction(
                        item.id!,
                      )}
                      onSelect={() => handleBoxSelect(item.id!)}
                      onDragStart={() => handleBoxDragStart(item.id!, coordinate)}
                      onDragMove={(newCoordinate) =>
                        handleBoxDragMove(item.id!, coordinate, newCoordinate)
                      }
                      onDragEnd={(newCoordinate) => {
                        setIsDraggingBox(false);
                        handleBoxDragEnd(item.id!, coordinate, newCoordinate);
                      }}
                      onTransformEnd={(newCoordinate) => {
                        coordinateManager.updateCoordinate(
                          item.id!,
                          newCoordinate,
                        );
                      }}
                      hideInfoOverlay={isDraggingBox}
                      onEdit={
                        onEditItemRequest
                          ? () => onEditItemRequest(item)
                          : undefined
                      }
                    />
                  );
                })}
              {itemsForCurrentPage
                .filter((item) => selectedBoxIds.has(item.id!))
                .map((item) => {
                  const coordinate = coordinateManager.getCoordinate(item.id!);
                  if (!coordinate) return null;

                  return (
                    <CoordinateBox
                      key={item.id}
                      flyerItem={item}
                      coordinate={coordinate}
                      isSelected={true}
                      isAIExtraction={coordinateManager.isAIExtraction(
                        item.id!,
                      )}
                      onSelect={() => handleBoxSelect(item.id!)}
                      onDragStart={() => handleBoxDragStart(item.id!, coordinate)}
                      onDragMove={(newCoordinate) =>
                        handleBoxDragMove(item.id!, coordinate, newCoordinate)
                      }
                      onDragEnd={(newCoordinate) => {
                        setIsDraggingBox(false);
                        handleBoxDragEnd(item.id!, coordinate, newCoordinate);
                      }}
                      onTransformEnd={(newCoordinate) => {
                        coordinateManager.updateCoordinate(
                          item.id!,
                          newCoordinate,
                        );
                      }}
                      hideInfoOverlay={isDraggingBox}
                      onEdit={
                        onEditItemRequest
                          ? () => onEditItemRequest(item)
                          : undefined
                      }
                    />
                  );
                })}
            </Layer>
          </FlyerCoordinateEditorContextProvider>
        </Stage>
      </StageContainer>

      <ControlsContainer>
        <NavigationContainer>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 2 }}>
            <Tooltip title="Add Box" PopperProps={{ style: { zIndex: 10001 } }}>
              <IconButton
                size="small"
                onClick={handleAddBox}
                sx={{ color: "#ff8038" }}
                data-testid="flyer-coordinate-btn-add-box"
              >
                <AddBoxIcon />
              </IconButton>
            </Tooltip>
            <Tooltip
              title="Delete Selected Box"
              PopperProps={{ style: { zIndex: 10001 } }}
            >
              <span>
                <IconButton
                  size="small"
                  onClick={handleDeleteBox}
                  disabled={selectedBoxIds.size === 0}
                  color="error"
                  data-testid="flyer-coordinate-btn-delete-box"
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={
                multiSelectEnabled
                  ? "Disable Multi-Select (one box at a time)"
                  : "Enable Multi-Select (select multiple boxes)"
              }
              PopperProps={{ style: { zIndex: 10001 } }}
            >
              <IconButton
                size="small"
                onClick={() => setMultiSelectEnabled(!multiSelectEnabled)}
                sx={{ color: multiSelectEnabled ? "#ff8038" : "inherit" }}
                data-testid="flyer-coordinate-btn-multi-select-toggle"
              >
                <SelectAllIcon />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={
                groupMoveEnabled
                  ? "Disable Group Move"
                  : multiSelectEnabled
                    ? "Enable Group Move (Move Selected Together) • Tip: Right-drag to multi-select"
                    : "Enable Group Move (Move Selected Together)"
              }
              PopperProps={{ style: { zIndex: 10001 } }}
            >
              <IconButton
                size="small"
                onClick={() => setGroupMoveEnabled(!groupMoveEnabled)}
                disabled={!multiSelectEnabled}
                sx={{ color: groupMoveEnabled ? "#ff8038" : "inherit" }}
                data-testid="flyer-coordinate-btn-group-move-toggle"
              >
                <OpenWithIcon />
              </IconButton>
            </Tooltip>
            <Tooltip
              title="Reset Current Page"
              PopperProps={{ style: { zIndex: 10001 } }}
            >
              <span>
                <IconButton
                  size="small"
                  onClick={handleResetCurrentPage}
                  disabled={
                    !currentImageData ||
                    (coordinateManager
                      .getModifiedCoordinates()
                      .filter(
                        (change) =>
                          change.modifiedCoordinate?.flyerPage ===
                          currentImageData.flyerIndex,
                      ).length === 0 &&
                      Array.from(deletedItemIds).filter(
                        (id) =>
                          allFlyerItems.find((item) => item.id === id)
                            ?.coordinate?.flyerPage ===
                          currentImageData.flyerIndex,
                      ).length === 0)
                  }
                  color="warning"
                  data-testid="flyer-coordinate-btn-reset-current-page"
                >
                  <RestartAltIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title="Reset All Pages"
              PopperProps={{ style: { zIndex: 10001 } }}
            >
              <span>
                <IconButton
                  size="small"
                  onClick={handleResetAll}
                  disabled={
                    coordinateManager.getModifiedCoordinates().length === 0 &&
                    deletedItemIds.size === 0 &&
                    newItems.length === 0
                  }
                  color="warning"
                  data-testid="flyer-coordinate-btn-reset-all-pages"
                >
                  <LayersClearIcon />
                </IconButton>
              </span>
            </Tooltip>
            {isAiGenerated && (
              <Tooltip
                title="Retry Current Image Coordinates"
                PopperProps={{ style: { zIndex: 10001 } }}
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={handleRetryImage}
                    disabled={
                      !_flyerItemListId ||
                      !_flyerId ||
                      !currentImageData ||
                      retryingImageId !== null
                    }
                    color="primary"
                    data-testid="flyer-coordinate-btn-retry-image"
                  >
                    {retryingImageId !== null ? (
                      <CircularProgress size={20} />
                    ) : (
                      <AutorenewIcon sx={{ color: "#ff8038" }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Zoom In" PopperProps={{ style: { zIndex: 10001 } }}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleZoomIn}
                  disabled={stageScale >= EDITOR_CONSTANTS.ZOOM_MAX}
                  data-testid="flyer-coordinate-btn-zoom-in"
                >
                  <ZoomInIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title="Zoom Out"
              PopperProps={{ style: { zIndex: 10001 } }}
            >
              <span>
                <IconButton
                  size="small"
                  onClick={handleZoomOut}
                  disabled={stageScale <= EDITOR_CONSTANTS.ZOOM_MIN}
                  data-testid="flyer-coordinate-btn-zoom-out"
                >
                  <ZoomOutIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                ml: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: "4px",
                border: "1px solid #e0e0e0",
                backgroundColor: "#f5f5f5",
              }}
            >
              <Typography
                variant="caption"
                sx={{ minWidth: "30px" }}
                data-testid="flyer-coordinate-zoom-level-display"
              >
                {(() => {
                  // Calculate zoom relative to fitted state (100% = content fits window)
                  if (!externalSize) return `${Math.round(stageScale * 100)}%`;
                  const fitScale = Math.min(
                    externalSize.width / containerSize.width,
                    externalSize.height / containerSize.height,
                  );
                  const relativeZoom = (stageScale / fitScale) * 100;
                  return `${Math.round(relativeZoom)}%`;
                })()}
              </Typography>
              <Tooltip
                title="Reset Zoom"
                PopperProps={{ style: { zIndex: 10001 } }}
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={handleResetZoom}
                    disabled={
                      stageScale === 1 &&
                      stagePosition.x === 0 &&
                      stagePosition.y === 0
                    }
                    sx={{ padding: "2px" }}
                    data-testid="flyer-coordinate-btn-reset-zoom"
                  >
                    <ZoomOutMapIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <PageNavigator
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />

          <SaveButtonContainer data-testid="flyer-coordinate-save-section">
            <Badge
              badgeContent={
                coordinateManager.getModifiedCoordinates().length +
                deletedItemIds.size
              }
              color="error"
              sx={{ mr: 1 }}
              data-testid="flyer-coordinate-unsaved-changes-badge"
            >
              <Typography variant="caption" color="textSecondary">
                Unsaved changes
              </Typography>
            </Badge>
            <Button
              size="small"
              variant="contained"
              startIcon={
                isSaving ? (
                  <CircularProgress size={14} />
                ) : (
                  <SaveIcon fontSize="small" />
                )
              }
              onClick={handleSave}
              disabled={
                (!coordinateManager.isDirty && deletedItemIds.size === 0) ||
                isSaving
              }
              data-testid="flyer-coordinate-btn-save-changes"
              sx={{
                backgroundColor: "#ff8038",
                textTransform: "capitalize",
                fontSize: "14px",
                "&:hover": {
                  backgroundColor: "#e6722f",
                },
              }}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </SaveButtonContainer>
        </NavigationContainer>

        <ThumbnailBar
          gfImages={gfImages}
          currentPage={currentPage}
          isLoading={isLoading}
          onPageClick={setCurrentPage}
        />
      </ControlsContainer>

      <ItemSelectionDialog
        open={openSelectionModal}
        onClose={closeSelectionModal}
        allFlyerItems={allFlyerItems}
        currentPage={currentPage}
        deletedItemIds={deletedItemIds}
        itemsWithCoordinates={itemsWithCoordinates}
        onSelectItem={handleSelectItem}
        onCreateNew={handleCreateNewFromModal}
        onAIExtract={handleAIExtract}
      />

      <SaveConfirmationModal
        open={openSaveConfirmModal}
        onClose={closeSaveConfirmModal}
        onConfirm={handleConfirmSave}
        changes={coordinateManager.getModifiedCoordinates()}
        deletedItems={flyerItems.filter((item) =>
          item.id ? deletedItemIds.has(item.id) : false,
        )}
        isSaving={isSaving}
      />

      <Snackbar
        open={retryMessage !== null}
        autoHideDuration={6000}
        onClose={() => setRetryMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        data-testid="flyer-coordinate-retry-message-snackbar"
      >
        <Alert
          onClose={() => setRetryMessage(null)}
          severity={retryMessage?.severity || "info"}
          sx={{ width: "100%" }}
        >
          {retryMessage?.text}
        </Alert>
      </Snackbar>

      <MuiConfirmationModal
        open={openRetryConfirmModal}
        handleCancel={() => setOpenRetryConfirmModal(false)}
        handleConfirm={handleConfirmRetryImage}
        title={t(messages.Retry_Image_Coordinates_Title())}
        description={
          <>
            {t(messages.Retry_Image_Coordinates_Description())}
            <br />
            <br />
            {t(messages.Retry_Image_Coordinates_BulletIntro())}
            <ul style={{ marginTop: "8px", paddingLeft: "20px" }}>
              <li>{t(messages.Retry_Image_Coordinates_BulletTime())}</li>
              <li>{t(messages.Retry_Image_Coordinates_BulletOverwrite())}</li>
            </ul>
          </>
        }
        warningMessage={t(messages.Retry_Image_Coordinates_Warning())}
        warningSeverity="info"
        confirmButtonColor="#ff8038"
        sx={{ zIndex: 10002 }}
      />
    </ViewerContainer>
  );
};

export default FlyerCoordinateEditor;
